import {
  AdventDoor,
  AdventDoorInvite,
  AdventDoorInviteDelivery,
} from "apps/user/learner/api/types";

/** A door's calendar slot, parsed out of the API's invite. */
export interface DoorInvite {
  hours: number;
  minutes: number;
  durationMinutes: number;
  /**
   * Set once the learner has asked for the invite. `delivery` is null when
   * the server names a channel this build does not know; `timeZone` is null
   * when the server leaves it out, and the device zone stands in.
   */
  sent: {
    startsAt: Date;
    timeZone: string | null;
    delivery: AdventDoorInviteDelivery | null;
  } | null;
}

const DELIVERIES: Record<AdventDoorInviteDelivery, true> = {
  COMPANY_CALENDAR: true,
  EMAIL: true,
};

const LOCAL_TIME = /^(\d{1,2}):(\d{2})(?::\d{2})?$/;

class MalformedInviteError extends Error {
  constructor(door: AdventDoor, problem: string) {
    // Never echo the values: they come straight from the API.
    super(`Door ${door.dayNumber} invite: ${problem}`);
    this.name = "MalformedInviteError";
  }
}

const isTimeZone = (value: string): boolean => {
  try {
    new Intl.DateTimeFormat("en", { timeZone: value });
  } catch {
    return false;
  }
  return true;
};

const isDelivery = (value: unknown): value is AdventDoorInviteDelivery =>
  typeof value === "string" &&
  Object.prototype.hasOwnProperty.call(DELIVERIES, value);

const parseTimeZone = (
  door: AdventDoor,
  invite: AdventDoorInvite
): string | null => {
  const timeZone = invite.timeZone ?? null;
  if (timeZone !== null && !isTimeZone(timeZone)) {
    throw new MalformedInviteError(door, `"timeZone" is not a known zone`);
  }
  return timeZone;
};

const parseSent = (
  door: AdventDoor,
  invite: AdventDoorInvite,
  timeZone: string | null
): DoorInvite["sent"] => {
  if (!invite.sentAt) return null;
  const startsAt = new Date(invite.startsAt ?? "");
  if (Number.isNaN(startsAt.getTime())) {
    throw new MalformedInviteError(door, `"startsAt" must be a date once sent`);
  }
  return {
    startsAt,
    timeZone,
    delivery: isDelivery(invite.delivery) ? invite.delivery : null,
  };
};

/**
 * Throws on a slot it cannot read; the sheet then leaves the invite out
 * rather than offer a time it would have to guess.
 */
export const parseDoorInvite = (
  door: AdventDoor,
  invite: AdventDoorInvite
): DoorInvite => {
  const match = LOCAL_TIME.exec(invite.localTime);
  const hours = match ? Number(match[1]) : NaN;
  const minutes = match ? Number(match[2]) : NaN;
  if (!(hours < 24 && minutes < 60)) {
    throw new MalformedInviteError(door, `"localTime" must be HH:MM`);
  }
  const { durationMinutes } = invite;
  if (!Number.isInteger(durationMinutes) || durationMinutes <= 0) {
    throw new MalformedInviteError(
      door,
      `"durationMinutes" must be a positive integer`
    );
  }
  return {
    hours,
    minutes,
    durationMinutes,
    sent: parseSent(door, invite, parseTimeZone(door, invite)),
  };
};

/** The slot's wall time, as "19:00" or "7:00 PM" by the learner's locale. */
export const formatInviteTime = (
  invite: DoorInvite,
  language: string
): string =>
  new Intl.DateTimeFormat(language, {
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(2000, 0, 1, invite.hours, invite.minutes));

/** The duration's translation key and count: whole hours, else minutes. */
export const inviteDuration = (
  invite: DoorInvite
): { key: string; count: number } =>
  invite.durationMinutes % 60 === 0
    ? { key: "decemberDoors:invite.hours", count: invite.durationMinutes / 60 }
    : { key: "decemberDoors:invite.minutes", count: invite.durationMinutes };

/**
 * The booked slot as "Sun 6 Dec, 19:00", in the zone the slot was offered
 * in, so it matches the time shown before sending wherever the device is.
 */
export const formatInviteStart = (
  sent: NonNullable<DoorInvite["sent"]>,
  language: string
): string =>
  new Intl.DateTimeFormat(language, {
    timeZone: sent.timeZone ?? undefined,
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  }).format(sent.startsAt);
